/**
 * @file        src/pages/hotel/RaiseHotelAmendmentsModal.jsx
 * @description The hotel "Amendments" popup, opened from the Booking Details
 *              drawer's Amendments card. Flight parity (RaiseFlightAmendmentsModal)
 *              with the hotel type set. Behaviour by group:
 *                • email  (7 types) → POST /amendment/email (records + notifies ops)
 *                • cancel (Cancellation) → hands off to the existing cancel flow —
 *                  the real supplier path; never raised as an email request.
 *              Special rule: a CANCELLED booking may only raise a Correction.
 * @author      Kinetika Labs
 * @created     2026-08-04
 * @changelog
 *   - 2026-08-04 Claude — Initial (hotel amendment queue, flight parity).
 *   - 2026-08-10 Claude — Amendment Type now uses the themed Select (console-wide
 *                          dropdown consistency pass) instead of a native <select>.
 *   - 2026-09-15 Claude — API errors read via apiErrorMessage(): the error handler returns {error:{code,message}}, and putting that object into state/toast threw "Objects are not valid as a React child", blanking the whole app.
 */
import { useMemo, useState } from 'react';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import { toast } from '../../lib/toast';
import { raiseEmailAmendment } from '../../api/hotelBookings';
import { apiErrorMessage } from '../../api/client';

// The hotel amendment types. `group` decides the behaviour (email vs cancel).
const AMENDMENT_OPTIONS = [
  { value: 'CANCELLATION', label: 'Cancellation', group: 'cancel' },
  { value: 'DATE_CHANGE', label: 'Date Change', group: 'email' },
  { value: 'ROOM_CHANGE', label: 'Room Change', group: 'email' },
  { value: 'GUEST_CHANGE', label: 'Guest Change', group: 'email' },
  { value: 'CORRECTION', label: 'Correction', group: 'email' },
  { value: 'CANCELLATION_QUOTATION', label: 'Cancellation Quotation', group: 'email' },
  { value: 'MISCELLANEOUS', label: 'Miscellaneous', group: 'email' },
  { value: 'CUSTOM', label: 'Custom', group: 'email' },
];

export default function RaiseHotelAmendmentsModal({ booking, onClose, onCancellation, onRaised }) {
  const isCancelled = /cancel/i.test(booking?.booking_status || '');

  // A cancelled booking may raise ONLY a Correction (same rule as flight).
  const options = useMemo(
    () => (isCancelled ? AMENDMENT_OPTIONS.filter((o) => o.value === 'CORRECTION') : AMENDMENT_OPTIONS),
    [isCancelled],
  );

  const [type, setType] = useState(options[0]?.value || 'CORRECTION');
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const selected = options.find((o) => o.value === type) || options[0];

  const raise = async () => {
    setErr('');
    if (!selected) return;

    // Cancellation → hand off to the existing preview→confirm cancel flow.
    if (selected.group === 'cancel') {
      onClose();
      onCancellation?.(booking);
      return;
    }

    // Email-only types → record + notify ops.
    setBusy(true);
    try {
      const res = await raiseEmailAmendment(booking.id, { type: selected.value, remarks: remarks.trim() });
      const d = res?.data || {};
      if (d.emailed) {
        toast.ok('Amendment raised', `${selected.label} recorded and the ops team was emailed.`);
      } else {
        toast.ok('Amendment recorded', `${selected.label} was logged for the ops team (email notification unavailable).`);
      }
      onRaised?.();
      onClose();
    } catch (e) {
      setErr(apiErrorMessage(e, 'Could not raise the amendment.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 480 }}>
        <div className="lk-modal-head">
          <h3>Amendments · #{booking.id}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>

        <div className="det-body">
          <div className="field full">
            <label>Booking Id</label>
            <input value={booking.booking_reference || ''} readOnly disabled />
          </div>

          <div className="field full">
            <label>Amendment Type</label>
            <Select
              value={type}
              onChange={(v) => { setType(v); setErr(''); }}
              options={options}
              disabled={busy}
            />
          </div>

          {isCancelled && (
            <p className="muted" style={{ fontSize: '.85em' }}>
              This booking is cancelled, so only a Correction amendment can be raised.
            </p>
          )}

          {selected?.group === 'cancel' && (
            <p className="muted" style={{ fontSize: '.85em' }}>
              Cancellation opens a refund preview so you can review the amount before confirming.
            </p>
          )}

          {selected?.group === 'email' && (
            <div className="field full">
              <label>Remarks {selected.value === 'CUSTOM' ? '(required detail for a custom request)' : '(optional)'}</label>
              <textarea
                rows={3}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Add any detail the ops team needs to action this."
                disabled={busy}
                maxLength={1000}
              />
            </div>
          )}

          {err ? <div className="field-error">{err}</div> : null}

          <div className="form-actions">
            <button className="btn ghost" onClick={onClose} disabled={busy}>Close</button>
            <button className="btn primary" onClick={raise} disabled={busy}>
              {busy ? 'Raising…' : selected?.group === 'cancel' ? 'Continue to cancel' : 'Raise'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
